import { Component } from '@theme/component'
import '@theme/gsap'
import { useReduced } from '@theme/maths'
import {
  supportsIntersectionObserver,
  useIntersection
} from '@theme/use-intersection'

const FOOTER_WATCH = {
  clearance: 16,
  resizeDelay: 150,
  selector: 'footer'
}

const DETAILS_WATCH = {
  selector: 'section.product-info'
}

const StickyFormSelector = {
  submit: '[ref="addToCartButton"]',
  submitLabel: '.link-click > span',
  submitLabelBox: '.link-click',
  ctaPrice: '.sticky-form__cta-value',
  ctaPriceHost: '.sticky-form__cta-price',
  variantId: 'input[name="id"]',
  shell: '[data-perfume-shell]',
  bar: '[data-perfume-bar]',
  panel: '[data-perfume-panel]',
  toggle: '[data-perfume-toggle]',
  chevron: '.sticky-form__chevron',
  reveal: '[data-perfume-reveal]'
}

const PERFUME_NAV = {
  ease: 'power4.out',
  expand: 0.65,
  reveal: 0.6,
  revealStagger: 0.03,
  collapse: 0.45,
  collapseReveal: 0.25,
  collapseStagger: 0.01,
  chevron: 0.4,
  chevronBack: 0.3
}

export class StickyFormComponent extends Component {
  #perfumeOpen = false
  #perfumeTimeline = null
  #openEnd = 0
  #heights = { closed: 0, open: 0 }
  #controller = null
  #footerMargin = 0
  #footerReached = false
  #resizeTimeout = 0
  #unobserveFooter = null
  #unobserveDetails = null

  #handleResize = () => {
    this.#remeasurePerfume()
    this.#refreshFooterMargin()
  }

  #handleDetailsIntersect = (entry) => {
    const bounds = entry?.boundingClientRect
    const passed =
      Boolean(bounds) && !entry.isIntersecting && bounds.bottom <= 0
    this.dataset.detailsPassed = String(passed)
  }

  #handleFooterIntersect = (entry) => {
    this.#footerReached = Boolean(entry?.isIntersecting)
    this.#syncFooterState()
  }

  #handleFocusOut = (event) => {
    if (this.contains(event.relatedTarget)) return
    this.dataset.footerVisible = String(this.#footerReached)
  }

  connectedCallback() {
    super.connectedCallback?.()
    this.#controller = new AbortController()
    this.#mountCtaPrice()
    this.#setupPerfumeNav()
    this.#watchFooter()
    this.#watchDetails()
    this.#watchResize()
  }

  disconnectedCallback() {
    super.disconnectedCallback?.()
    this.#controller?.abort()
    this.#controller = null
    window.clearTimeout(this.#resizeTimeout)
    this.#unobserveFooter?.()
    this.#unobserveFooter = null
    this.#unobserveDetails?.()
    this.#unobserveDetails = null
    this.#footerReached = false
    delete this.dataset.footerVisible
    delete this.dataset.detailsPassed
  }

  handleVariantChange(event) {
    const option = event.target
    if (!(option instanceof HTMLInputElement)) {
      return
    }

    const variantId = this.querySelector(StickyFormSelector.variantId)
    if (!variantId) {
      return
    }

    variantId.value = option.value
    this.#syncSubmit(option)
  }

  handlePerfumeToggle() {
    if (!this.#perfumeTimeline) {
      this.#setPerfumeState(!this.#perfumeOpen)
      return
    }

    this.#setPerfumeState(!this.#perfumeOpen)
    const timeline = this.#perfumeTimeline

    if (this.#perfumeOpen) {
      timeline.invalidate()
      if (timeline.time() >= this.#openEnd) timeline.restart()
      else timeline.play()
      return
    }

    if (timeline.time() < this.#openEnd) timeline.reverse()
    else timeline.play()
  }

  #mountCtaPrice() {
    const price = this.querySelector(StickyFormSelector.ctaPriceHost)
    const button = this.querySelector(StickyFormSelector.submit)
    if (!(price && button) || button.contains(price)) {
      return
    }

    const labelBox = button.querySelector(StickyFormSelector.submitLabelBox)
    if (labelBox) {
      labelBox.after(price)
      return
    }
    button.append(price)
  }

  #syncSubmit(option) {
    const button = this.querySelector(StickyFormSelector.submit)
    if (!button) {
      return
    }

    const isAvailable = option.dataset.variantAvailable === 'true'
    button.disabled = !isAvailable
    button.setAttribute('aria-disabled', String(!isAvailable))
    this.#syncSubmitLabel(button, option.dataset.addToCartText)
    this.#syncCtaPrice(option.dataset.variantPrice)
  }

  #syncCtaPrice(price) {
    if (!price) {
      return
    }

    const value = this.querySelector(StickyFormSelector.ctaPrice)
    if (value) {
      value.textContent = price
    }
  }

  #syncSubmitLabel(button, text) {
    if (!text) {
      return
    }

    button.setAttribute('aria-label', text)
    const label = button.querySelector(StickyFormSelector.submitLabel)
    if (label) {
      label.textContent = text
    }
  }

  #perfumeParts() {
    const shell = this.querySelector(StickyFormSelector.shell)
    const bar = this.querySelector(StickyFormSelector.bar)
    const panel = this.querySelector(StickyFormSelector.panel)
    const toggle = this.querySelector(StickyFormSelector.toggle)
    if (!(shell && bar && panel && toggle)) return null

    return {
      shell,
      bar,
      panel,
      toggle,
      chevron: toggle.querySelector(StickyFormSelector.chevron),
      reveals: panel.querySelectorAll(StickyFormSelector.reveal)
    }
  }

  #measurePerfumeHeights(parts) {
    const previous = parts.shell.style.height
    parts.shell.style.height = 'auto'
    const open = parts.shell.offsetHeight
    parts.shell.style.height = previous
    return { closed: parts.bar.offsetHeight, open }
  }

  #setupPerfumeNav() {
    const parts = this.#perfumeParts()
    if (!parts) return

    this.dataset.perfumeReady = 'true'
    this.#heights = this.#measurePerfumeHeights(parts)

    const gsap = window.gsap
    if (!gsap || useReduced()) return
    gsap.set(parts.shell, { height: this.#heights.closed })
    this.#perfumeTimeline = this.#buildPerfumeTimeline(parts, gsap)
  }

  #buildPerfumeTimeline(parts, gsap) {
    const timeline = gsap.timeline({
      paused: true,
      defaults: { ease: PERFUME_NAV.ease }
    })

    timeline
      .to(
        parts.shell,
        { height: () => this.#heights.open, duration: PERFUME_NAV.expand },
        0
      )
      .set(parts.panel, { autoAlpha: 1 }, 0.1)
      .fromTo(
        parts.reveals,
        { autoAlpha: 0, yPercent: 100 },
        {
          autoAlpha: 1,
          yPercent: 0,
          duration: PERFUME_NAV.reveal,
          stagger: PERFUME_NAV.revealStagger
        },
        0.1
      )

    if (parts.chevron) {
      timeline.to(
        parts.chevron,
        {
          rotate: 180,
          duration: PERFUME_NAV.chevron,
          ease: 'back.out(2)'
        },
        0.05
      )
    }

    this.#openEnd = timeline.duration()
    timeline.addPause()

    timeline
      .to(parts.reveals, {
        autoAlpha: 0,
        yPercent: 10,
        duration: PERFUME_NAV.collapseReveal,
        stagger: { each: PERFUME_NAV.collapseStagger, from: 'end' }
      })
      .to(
        parts.shell,
        {
          height: () => this.#heights.closed,
          duration: PERFUME_NAV.collapse,
          ease: 'power3.inOut'
        },
        '<'
      )

    if (parts.chevron) {
      timeline.to(
        parts.chevron,
        { rotate: 0, duration: PERFUME_NAV.chevronBack, ease: 'power3.in' },
        '<'
      )
    }

    return timeline.set(parts.panel, { autoAlpha: 0 })
  }

  #watchResize() {
    window.addEventListener(
      'resize',
      () => {
        window.clearTimeout(this.#resizeTimeout)
        this.#resizeTimeout = window.setTimeout(
          this.#handleResize,
          FOOTER_WATCH.resizeDelay
        )
      },
      { signal: this.#controller?.signal }
    )
  }

  #remeasurePerfume() {
    const gsap = window.gsap
    const parts = this.#perfumeParts()
    if (!(gsap && parts && this.#perfumeTimeline)) return

    this.#heights = this.#measurePerfumeHeights(parts)
    if (this.#perfumeOpen) {
      gsap.set(parts.shell, { height: this.#heights.open })
      return
    }
    this.#perfumeTimeline.invalidate()
    gsap.set(parts.shell, { height: this.#heights.closed })
  }

  #footerClearance() {
    const measured = this.#perfumeOpen
      ? this.#heights.open
      : this.#heights.closed
    return Math.round((measured || this.offsetHeight) + FOOTER_WATCH.clearance)
  }

  #watchFooter() {
    const footer = document.querySelector(FOOTER_WATCH.selector)
    if (!(footer && supportsIntersectionObserver())) return

    this.#unobserveFooter?.()
    this.#footerMargin = this.#footerClearance()
    this.#unobserveFooter = useIntersection({
      rootMargin: `0px 0px ${this.#footerMargin}px 0px`
    }).observe(footer, this.#handleFooterIntersect)

    this.addEventListener('focusout', this.#handleFocusOut, {
      signal: this.#controller?.signal
    })
  }

  #watchDetails() {
    const details = document.querySelector(DETAILS_WATCH.selector)
    if (!(details && supportsIntersectionObserver())) {
      this.dataset.detailsPassed = 'true'
      return
    }

    this.dataset.detailsPassed = 'false'
    this.#unobserveDetails = useIntersection({ threshold: 0 }).observe(
      details,
      this.#handleDetailsIntersect
    )
  }

  #refreshFooterMargin() {
    if (!this.#unobserveFooter) return
    if (this.#footerClearance() === this.#footerMargin) return
    this.#watchFooter()
  }

  #syncFooterState() {
    if (this.#footerReached && this.contains(document.activeElement)) return
    this.dataset.footerVisible = String(this.#footerReached)
  }

  #setPerfumeState(open) {
    const parts = this.#perfumeParts()
    if (!parts) return

    this.#perfumeOpen = open
    parts.toggle.setAttribute('aria-expanded', String(open))
    parts.panel.setAttribute('aria-hidden', String(!open))
    this.#refreshFooterMargin()
  }
}

if (!customElements.get('sticky-form')) {
  customElements.define('sticky-form', StickyFormComponent)
}
